gsap.registerPlugin(CustomEase);


CustomEase.create("smooth", "0.77,0,0.175,1");

class SideDrawer extends HTMLElement {
    constructor() {
        super()
        this.isOpen = false
        this.modalId = this.dataset.modal
        this.backdrop = this.querySelector('[data-modal="bg"]')
        this.content = this.querySelector('[data-modal="content"]')
        this.closeBtn = this.querySelector('[data-modal="close"]')
        this.focusable = []
        this.first = null
        this.last = null
        this.previousActive = null

        this.timeline = null

        this.handleKeydown = this.handleKeydown.bind(this)
        this.handleDocumentClick = this.handleDocumentClick.bind(this)
        this.handleTouchStart = this.handleTouchStart.bind(this)
        this.handleTouchMove = this.handleTouchMove.bind(this)
        this.touchStartX = 0
        this.touchLastY = 0
    }

    connectedCallback() {
        this.setAttribute('aria-hidden', 'true')
        this.hidden = true

        this.createTimeline()

        this.closeBtn?.addEventListener('click', () => this.close())
        this.backdrop?.addEventListener('click', () => this.close())

        document.addEventListener('click', this.handleDocumentClick)

        // Keep touch scrolling inside the drawer (e.g. the country list) instead of
        // letting iOS scroll the page or the mobile menu behind it.
        this.style.overscrollBehavior = 'contain'
        this.querySelectorAll('[data-scroll-container], [data-panel]').forEach((el) => {
            el.style.overscrollBehavior = 'contain'
        })
        this.addEventListener('touchstart', this.handleTouchStart, { passive: true })
        this.addEventListener('touchmove', this.handleTouchMove, { passive: false })

        // When the keyboard closes while the drawer stays open, put the page back already
        this.addEventListener('focusout', (e) => {
            if (!this.isOpen || !e.target.matches?.('input, textarea, select')) return
            this.waitForKeyboardToClose().then(() => {
                if (this.isOpen) this.restoreScroll()
            })
        })
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick)
        this.removeEventListener('touchstart', this.handleTouchStart)
        this.removeEventListener('touchmove', this.handleTouchMove)
    }

    handleTouchStart(e) {
        const touch = e.touches[0]
        this.touchStartX = touch.clientX
        this.touchLastY = touch.clientY
    }

    handleTouchMove(e) {
        if (!this.isOpen || !e.cancelable) return

        const touch = e.touches[0]
        const dy = touch.clientY - this.touchLastY
        const dx = Math.abs(touch.clientX - this.touchStartX)
        this.touchLastY = touch.clientY

        // Horizontal swipes are left alone (sliders inside a drawer)
        if (dx > Math.abs(dy) || dy === 0) return

        const target = e.target instanceof Element ? e.target : null

        // While a dropdown (e.g. the country list) is open, only that list may scroll.
        // Swiping anywhere else is blocked, so nothing behind it moves.
        const openPanel = this.querySelector('[data-panel]:not([hidden])')
        if (openPanel && !openPanel.contains(target)) {
            e.preventDefault()
            return
        }
        const boundary = openPanel ? openPanel.parentElement : this

        // Allow the swipe only if an element can still scroll in that direction;
        // otherwise iOS would scroll the page behind it.
        let el = target
        while (el && el !== boundary) {
            const overflowY = getComputedStyle(el).overflowY
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
                const atTop = el.scrollTop <= 0
                const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
                if ((dy > 0 && !atTop) || (dy < 0 && !atBottom)) return
            }
            el = el.parentElement
        }

        e.preventDefault()
    }

    handleDocumentClick(e) {
        const trigger = e.target.closest(`[data-drawer-trigger="${this.modalId}"]`)
        if (trigger) {
            e.preventDefault()
            this.open(trigger)
        }
    }

    createTimeline() {
        this.timeline = gsap.timeline({
            paused: true,
            defaults: {
                duration: 0.6,
                ease: 'smooth',
            },
            onReverseComplete: () => {
                this.hidden = true
                this.setAttribute('aria-hidden', 'true')
            }
        })

        this.timeline
            .set(this, { display: 'flex' })
            .from(this.content, { x: '100%' })
            .fromTo(this.backdrop, { opacity: 0 }, { opacity: 0.3 }, 0)
            // Mobile draws the dim as a shadow of the panel instead (see theme.liquid)
            .fromTo(this, { '--modal-dim': 0 }, { '--modal-dim': 1 }, 0)
    }

    open(trigger) {
        if (this.isOpen) return
        this.isOpen = true
        this.previousActive = trigger

        // iOS scrolls the page when the keyboard opens (e.g. country search), even
        // while it is locked. Remember the position so close() can put it back.
        this.savedScrollY = window.scrollY
        clearTimeout(this.unlockTimer)
        document.documentElement.dataset.modalScrollLock = ''

        this.hidden = false
        this.setAttribute('aria-hidden', 'false')
        // Desktop: body overflow 'clip' instead of 'hidden'. With html also hidden, a hidden
        // body becomes its own scroll container, so sticky elements (the navbar, the reviews
        // summary card) let go and jump out of view while the drawer is open. Clip locks the
        // same way without that. Mobile keeps 'hidden' (scroll lock verified on iPhone).
        if (window.matchMedia('(min-width: 64rem)').matches) document.body.style.overflow = 'clip'
        document.documentElement.classList.add('overflow-hidden')
        document.body.classList.add('overflow-hidden')



        this.setupFocusTrap()

        this.timeline.play()

        requestAnimationFrame(() => {
            ScrollTrigger.getAll().forEach((st) => {
                if (this.contains(st.trigger)) {
                    st.refresh();
                }
            });
        });


        document.addEventListener('keydown', this.handleKeydown)
    }

    close() {
        if (!this.isOpen) return
        this.isOpen = false
        document.removeEventListener('keydown', this.handleKeydown)

        // If the keyboard is open (e.g. country search), close it first and wait until
        // it is gone: iOS scrolls the page for the keyboard, and we want the page back in
        // place while the drawer still covers it. Otherwise the header visibly drops in.
        const active = document.activeElement
        const keyboardOpen = this.contains(active) && active.matches?.('input, textarea, select')
        if (this.contains(active)) active.blur()

        const finish = () => {
            this.restoreScroll()
            clearTimeout(this.unlockTimer)
            this.unlockTimer = setTimeout(() => {
                this.restoreScroll()
                // Let the auto-hide header react to scrolling again once the page is back
                requestAnimationFrame(() => {
                    delete document.documentElement.dataset.modalScrollLock
                })
            }, 350)

            document.documentElement.classList.remove('overflow-hidden')
            document.body.classList.remove('overflow-hidden')
            document.body.style.removeProperty('overflow')
            this.timeline.reverse()

            // preventScroll: focus() would otherwise scroll the page to the trigger before
            // everything behind the drawer is back in place (e.g. the sticky reviews card),
            // making the page jump down and then back.
            this.previousActive?.focus({ preventScroll: true })
        }

        if (keyboardOpen) {
            this.waitForKeyboardToClose().then(finish)
        } else {
            finish()
        }
    }

    // Resolves once the on-screen keyboard has closed (visual viewport back to full
    // height), or after 500ms at most.
    waitForKeyboardToClose() {
        return new Promise((resolve) => {
            const vv = window.visualViewport
            let done = false
            let timer
            const end = () => {
                if (done) return
                done = true
                clearTimeout(timer)
                vv?.removeEventListener('resize', check)
                requestAnimationFrame(() => resolve())
            }
            const check = () => {
                if (!vv || vv.height >= window.innerHeight - 1) end()
            }
            timer = setTimeout(end, 500)
            vv?.addEventListener('resize', check)
            setTimeout(check, 50)
        })
    }

    restoreScroll() {
        if (typeof this.savedScrollY === 'number' && window.scrollY !== this.savedScrollY) {
            // 'instant': the theme sets scroll-behavior: smooth on <html>, which would
            // animate this jump and make the header visibly slide back into place.
            window.scrollTo({ top: this.savedScrollY, left: 0, behavior: 'instant' })
        }
    }

    setupFocusTrap() {
        this.focusable = this.querySelectorAll(
            'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )

        this.first = this.focusable[0]
        this.last = this.focusable[this.focusable.length - 1]

        requestAnimationFrame(() => {
            this.first?.focus()
        })
    }

    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.close()
            return
        }

        if (e.key !== 'Tab') return

        if (e.shiftKey && document.activeElement === this.first) {
            e.preventDefault()
            this.last.focus()
        }

        if (!e.shiftKey && document.activeElement === this.last) {
            e.preventDefault()
            this.first.focus()
        }
    }
}

customElements.define('side-drawer', SideDrawer)
